import React from 'react';
import isNil from 'lodash/isNil';
import capitalize from 'lodash/capitalize';
import { Html4Entities } from 'html-entities';
import dayjs from 'dayjs';

/*
 * Returns fallback text if the passed in string is nullish
 *
 * fallback text defaults to `--`
 */
export const orFallback = (value: string | null | undefined, fallback = '--'): string => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  return value;
};

/**
 * Compares two values by converting them first to strings, and then comparing.
 * This is useful since a lot of times we receive data from services as either a number or string for Id's
 */
export const stringCompare = (
  valueA?: number | string | null,
  valueB?: number | string | null,
  ignoreNullish?: boolean
) => {
  if (ignoreNullish && (isNil(valueA) || isNil(valueB))) {
    return false;
  }

  return String(valueA) === String(valueB);
};

export const capitalizeEachWord = (string: string) => {
  if (string) {
    const output = string
      .split(' ')
      .map(s => capitalize(s))
      .join(' ');

    return output;
  }
  return string;
};

export const unCamelCase = (
  string: string,
  opts: { capitalize: boolean } = { capitalize: true }
) => {
  let result = string.split(/(?=[A-Z])/g).join(' ');

  if (opts && opts.capitalize) {
    result = capitalizeEachWord(result);
  }

  return result;
};

/**
 * Used for building element ids for test automation when we need to dynamically pull a property name from a component.
 * Naming convention is all lower kabob case: foo-bar-something
 * @param string the original string used to start building the element id
 * @param suffix the optional suffix to append to the original string (e.g., `-expandable-list-item`)
 */
export const convertToLowerKabobCase = (string: string, suffix?: string) => {
  if (!string) {
    return '';
  }
  let convertedString = string
    .trim()
    .replace(/:/g, '')
    .replace(/ /g, '-')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .replace(/\//g, '-')
    .replace(/,/g, '-')
    .replace(/\./g, '-');

  if (suffix) {
    convertedString = convertedString.concat(suffix);
  }

  return convertedString.toLowerCase();
};

/**
 * Takes in a count, word, suffx, override to change the plural form of the word
 * @param count count for singular or plural
 * @param word word to pluralize
 * @param suffixOrOverride suffix or complete override for plural form of word
 * @param override toggle whether to override plural form of word
 */
export const pluralize = (
  count: number,
  word: string,
  suffixOrOverride = 's',
  override?: boolean
) => {
  let plural = '';

  if (override) {
    plural = suffixOrOverride;
  } else if (suffixOrOverride === 'ies') {
    plural = word.slice(0, -1) + suffixOrOverride;
  } else {
    plural = word + suffixOrOverride;
  }

  return count === 1 ? word : plural;
};

/**
 * Takes in string and optional matcher and returns length of the matched string
 * @param string string to get length for
 * @param matcher RegExp to match against
 */
export function stringLength(string: string, matcher = /./g): number {
  const match = string.match(matcher);
  return match ? match.join('').length : 0;
}

export const noOrphan = (str: string, decode = true) => {
  const entities = new Html4Entities();
  const arr = str.split('');
  arr[arr.lastIndexOf(' ')] = decode ? entities.decode('&nbsp;') : '&nbsp;';
  return arr.join('');
};

/**
 * toSentenceCase is not a true to sentence case in that it will strip out dashes or underscores
 * rather it simply seeks to make the first letter of the string capitalized and the rest
 * lowercase. This implementation also preserves the use of acronyms but will convert to
 * sentence case if all caps are passed to it ignoring possible acronyms in all caps strings.
 * This function was built mostly to convert button text to sentence case while allowing
 * certain grammatical needs.
 */
export const toSentenceCase = (str: string | React.ReactNode) => {
  if (typeof str === 'string') {
    const isAllCaps = str === str.toUpperCase();
    const isDividerChar = (value: string) => value === ' ' || value === '-' || value === '_';
    return str
      .split('')
      .map((val, i, arr) => {
        // Convert first Letter to uppercase
        if (i === 0) return val.toUpperCase();
        // if string is all caps convert to sentence case and ignore possible acronymns
        if (!isAllCaps) {
          // if sring is not all caps preserve possible acronymns
          // Preserve Acronyms
          // if current val is uppercase check if val before or after is uppercase
          const isCurrentUpperCase = val === val.toUpperCase();
          if (isCurrentUpperCase) {
            const beforeVal = arr[i - 1];
            const afterVal = arr[i + 1];
            const isBeforeUpperCaseAndNotZero =
              i - 1 !== 0 && beforeVal === beforeVal.toUpperCase() && !isDividerChar(beforeVal);
            const isAfterUpperCaseAndNotUndefined =
              afterVal !== undefined &&
              afterVal === afterVal.toUpperCase() &&
              !isDividerChar(afterVal);
            // if val before or aftr is uppercase, do not lowercase unless before is index 0 or after is undefined
            if (isBeforeUpperCaseAndNotZero || isAfterUpperCaseAndNotUndefined) {
              return val;
            }
          }
        }
        // default to lowercase
        return val.toLowerCase();
      })
      .join('');
  }
  return str;
};

export const ordinalSuffix = (number: number) => {
  const mod10 = number % 10;
  const mod100 = number % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return `${number}st`;
  }
  if (mod10 === 2 && mod100 !== 12) {
    return `${number}nd`;
  }
  if (mod10 === 3 && mod100 !== 13) {
    return `${number}rd`;
  }
  return `${number}th`;
};

/**
 * wordsObjectForBoldStyling is initially being created for components where we want to select
 * the words we want to display with bold styling.
 * @param sentence a string in where we want to put a mark on the words we want to make bold
 * @param boldWords an array of numbers, marking which words we want to affect
 * @returns an array with objects {word:string,isBold:bolean,key:string}
 */

export type BuildedElement = { word: string; isBold: boolean } | string;

export const wordsObjectForBoldStyling = (sentence: string, boldWords: number[]) => {
  const scopedSentence: BuildedElement[] = sentence.split(' ');
  boldWords.forEach(idx => {
    scopedSentence.splice(idx - 1, 1, { word: `${scopedSentence[idx - 1]}`, isBold: true });
  });

  return scopedSentence;
};
